import { useState, useCallback, useEffect } from 'react'
import type { Map as MapboxMap } from 'mapbox-gl'
import {
  calculateDistance,
  formatDistance,
  getMidpoint,
} from '../../utils/geometry'

interface MeasurementPoint {
  lng: number
  lat: number
}

interface UseMeasurementReturn {
  isMeasuring: boolean
  measurementPoints: MeasurementPoint[]
  previewPoint: MeasurementPoint | null
  distance: number | null
  formattedDistance: string
  startMeasurement: () => void
  stopMeasurement: () => void
  clearMeasurement: () => void
  handleMapClick: (e: mapboxgl.MapMouseEvent) => void
  handleMapMouseMove: (e: mapboxgl.MapMouseEvent) => void
}

export const useMeasurement = (map: MapboxMap | null): UseMeasurementReturn => {
  const [isMeasuring, setIsMeasuring] = useState(false)
  const [measurementPoints, setMeasurementPoints] = useState<
    MeasurementPoint[]
  >([])
  const [previewPoint, setPreviewPoint] = useState<MeasurementPoint | null>(
    null
  )
  const [distance, setDistance] = useState<number | null>(null)

  // Start measurement mode
  const startMeasurement = useCallback(() => {
    setIsMeasuring(true)
    setMeasurementPoints([])
    setDistance(null)
  }, [])

  // Stop measurement mode
  const stopMeasurement = useCallback(() => {
    setIsMeasuring(false)
  }, [])

  // Clear current measurement
  const clearMeasurement = useCallback(() => {
    setMeasurementPoints([])
    setPreviewPoint(null)
    setDistance(null)
  }, [])

  // Handle map mouse move for preview
  const handleMapMouseMove = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      if (!isMeasuring) return

      const point: MeasurementPoint = {
        lng: e.lngLat.lng,
        lat: e.lngLat.lat,
      }

      // Only show preview if we have exactly one point
      if (measurementPoints.length === 1) {
        setPreviewPoint(point)

        // Calculate preview distance
        const dist = calculateDistance(
          [measurementPoints[0].lng, measurementPoints[0].lat],
          [point.lng, point.lat]
        )
        setDistance(dist)
      } else {
        setPreviewPoint(null)
      }
    },
    [isMeasuring, measurementPoints]
  )

  // Handle map click during measurement
  const handleMapClick = useCallback(
    (e: mapboxgl.MapMouseEvent) => {
      if (!isMeasuring) return

      const point: MeasurementPoint = {
        lng: e.lngLat.lng,
        lat: e.lngLat.lat,
      }

      setMeasurementPoints((prev) => {
        // If we already have 2 points, start a new measurement
        if (prev.length >= 2) {
          setPreviewPoint(null)
          setDistance(null)
          return [point]
        }

        const newPoints = [...prev, point]

        // Calculate distance when we have 2 points
        if (newPoints.length === 2) {
          const dist = calculateDistance(
            [newPoints[0].lng, newPoints[0].lat],
            [newPoints[1].lng, newPoints[1].lat]
          )
          setDistance(dist)
          setPreviewPoint(null)
        }

        return newPoints
      })
    },
    [isMeasuring]
  )

  // Handle ESC key to cancel measurement
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMeasuring) {
        stopMeasurement()
        clearMeasurement()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isMeasuring, stopMeasurement, clearMeasurement])

  // Change cursor when measuring
  useEffect(() => {
    if (!map) return

    if (isMeasuring) {
      map.getCanvas().style.cursor = 'crosshair'
    } else {
      map.getCanvas().style.cursor = ''
    }
  }, [map, isMeasuring])

  const formattedDistance = distance ? formatDistance(distance) : ''

  return {
    isMeasuring,
    measurementPoints,
    previewPoint,
    distance,
    formattedDistance,
    startMeasurement,
    stopMeasurement,
    clearMeasurement,
    handleMapClick,
    handleMapMouseMove,
  }
}
